import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import ClientRow from "./ClientRow";
import NewClient from "../../components/Modals/Create/NewClient";
import { BsPersonFillAdd } from "react-icons/bs";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Card, Table, Form, InputGroup, Spinner, Pagination } from "react-bootstrap";
import { useClients } from "../../hooks/useGetClients";
import { useQueryClient } from "@tanstack/react-query";
import { getClients } from "../../api/CLIENTS/clients";
import { useLocation } from "react-router-dom";

const Clients = ({ titre }) => {
  const { hasPermission } = useAuth();
  const location = useLocation();

  const restoredListState = location.state?.listState;


  const [search, setSearch] = useState(restoredListState?.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(restoredListState?.search || "");
  const [page, setPage] = useState(restoredListState?.page || 1);
  const [limit, setLimit] = useState(restoredListState?.limit || 10);
  const [showNewClient, setShowNewClient] = useState(false);
  // Détecte si l'utilisateur est sur mobile (< 1000px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);
  const queryClient = useQueryClient();


  const activeSearch = debouncedSearch.trim().length >= 3 ? debouncedSearch.trim() : "";

  const { data, isLoading, isError } = useClients(page, limit, activeSearch);

  const clients = Array.isArray(data?.data?.clients) ? data.data.clients : [];
  const totalItems = data?.data?.total ?? data?.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));

  const testClients = clients.map((c) => ({
    id: c.id,
    nom: c.name,
    adresse: c.address,
    email: c.email,
    telephone: c.phone_number,
    contracts: c.contracts ?? [],
  }));

  // Debounce: attend 500ms après la dernière frappe avant de déclencher la requête.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Prefetch next page
  useEffect(() => {
    if (page < totalPages) {
      queryClient.prefetchQuery({
        queryKey: ["clients", page + 1, limit, activeSearch],
        queryFn: () => getClients(page + 1, limit, activeSearch),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [page, limit, activeSearch, totalPages, queryClient]);

  // Restore scroll position
  useEffect(() => {
    if (restoredListState?.scrollY && !isLoading) {
      window.scrollTo(0, restoredListState.scrollY);
    }
  }, [restoredListState, isLoading]);

  // Bascule entre card et tableau selon la largeur d'écran
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1000);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const listState = { page, limit, search };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const getPageNumbers = (current, total) => {
    const pages = [];
    const delta = 2;
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      const left = Math.max(2, current - delta);
      const right = Math.min(total - 1, current + delta);
      if (left > 2) pages.push("...");
      for (let i = left; i <= right; i++) pages.push(i);
      if (right < total - 1) pages.push("...");
      pages.push(total);
    }
    return pages;
  };

  const isSearchTooShort = search.trim().length > 0 && search.trim().length < 3;

  return (
    <>
      <NewClient showNewClient={showNewClient} setShowNewClient={setShowNewClient} />

      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">{titre}</h1>
        {hasPermission('CREATE_CLIENT') && (
          <button 
            className="btn btn-client btn-icon-split btn-primary ms-2 mb-0" 
            onClick={() => setShowNewClient(true)}
          >
            <span
              className="icon text-white-50"
              style={{ alignItems: "center", justifyContent: "center" }}
            >
              <BsPersonFillAdd style={{ color: "#fff" }} />
            </span>
            <span className="text">Ajouter un client</span>
          </button>
        )}
      </div>

      <Card className="border-left-primary shadow mb-4">
        <Card.Body>
          <div
            className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3"
            style={{ width: "100%" }}
          >
            <div style={{ flex: "1 1 150px", maxWidth: "70px" }}>
              <Form.Select
                value={limit}
                onChange={handleLimitChange}
                disabled={isLoading}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
            </div>

            <div style={{ flex: "2 1 200px", maxWidth: "350px" }}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Recherche par nom ou téléphone"
                  value={search}
                  onChange={handleSearchChange}
                  className={`before-input ${isSearchTooShort ? "is-invalid" : ""}`}
                />
                {isSearchTooShort && (
                  <div className="invalid-feedback">
                    Minimum 3 caractères pour lancer la recherche.
                  </div>
                )}
              </InputGroup>
            </div>

            <div
              className="d-flex justify-content-end"
              style={{ flex: "3 1 250px", minWidth: "250px" }}
            >
              <Pagination className="mb-0 flex-wrap">
                <Pagination.Prev
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  {isMobile ? <FaChevronLeft /> : "Précédent"}
                </Pagination.Prev>

                {getPageNumbers(page, totalPages).map((p, idx) =>
                  p === "..." ? (
                    <Pagination.Ellipsis key={`top-ellipsis-${idx}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={`top-page-${p}-${idx}`}
                      active={p === page}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </Pagination.Item>
                  )
                )}

                <Pagination.Next
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  {isMobile ? <FaChevronRight /> : "Suivant"}
                </Pagination.Next>
              </Pagination>
            </div>
          </div>

          {/* Show hint instead of table when search is too short */}
          {isSearchTooShort ? (
            <div className="text-center text-muted py-5">
              <p className="mb-0">
                Saisissez au moins <strong>3 caractères</strong> pour effectuer une recherche.
              </p>
            </div>
          ) : isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" style={{ color: "#4e73df" }}>
                <span className="visually-hidden">Chargement...</span>
              </Spinner>
            </div>
          ) : isError ? (
            <div className="alert alert-danger">
              Erreur lors du chargement des clients
            </div>
          ) : (
            <>
              {/* Sur mobile : cards. Sur desktop : tableau */}
              {isMobile ? (
                <div className="row g-3">
                  {testClients.length === 0 ? (
                    <div className="col-12">
                      <div className="alert alert-info text-center mb-0">Aucun client trouvé</div>
                    </div>
                  ) : (
                    testClients.map((client, idx) => (
                      // Use index fallback to keep keys unique if API returns duplicate ids
                      <div key={client.id ?? `client-${idx}`} className="col-12">
                        <ClientRow client={client} listState={listState} asCard />
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <Table striped hover responsive className="align-middle">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Adresse</th>
                      <th>Email</th>
                      <th>Téléphone</th>
                      <th>Contrat</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testClients.map((client, idx) => (
                      // Use index fallback to keep keys unique if API returns duplicate ids
                      <ClientRow key={client.id ?? `client-${idx}`} client={client} listState={listState} />
                    ))}
                    {testClients.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center">
                          Aucun client trouvé
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}

              {/* Footer: total count and repeated pagination for convenience */}
              <div className="d-flex flex-column gap-3 mt-3">

                {/* Bottom pagination controls */}
                <Pagination className="mb-0 flex-wrap">
                  <Pagination.Prev
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    {isMobile ? <FaChevronLeft /> : "Précédent"}
                  </Pagination.Prev>

                  {getPageNumbers(page, totalPages).map((p, idx) =>
                    p === "..." ? (
                      <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                    ) : (
                      <Pagination.Item
                        // prefix numeric page key to avoid accidental duplication with other keys
                        key={`page-${p}-${idx}`}
                        active={p === page}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </Pagination.Item>
                    )
                  )}

                  <Pagination.Next
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    {isMobile ? <FaChevronRight /> : "Suivant"}
                  </Pagination.Next>
                </Pagination>

                <div>Total : {totalItems} clients</div>

              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default Clients;